import { useRef, useState, type KeyboardEvent } from 'react'
import { api } from './api.ts'
import { fetchRequisition, type SyncRequisition } from './fetchRequisition.ts'
import {
  findRequisitions,
  GoToStatus,
  movedActive,
  type GoToState,
  type RequisitionMatch,
  type SearchRequisitions,
  type WantedRequisition,
} from './goToRequisition.ts'

const IDLE: GoToState = { status: GoToStatus.Idle }

export interface GoToRequisition {
  readonly value: string
  readonly state: GoToState
  change(value: string): void
  /** Enter: looks the value up, or opens the option the list is on. */
  submit(): void
  keyDown(event: KeyboardEvent<HTMLInputElement>): void
  pick(match: RequisitionMatch): void
  /** Puts the list and the message away; what was typed stays. */
  dismiss(): void
}

/** The "Ir para conferência" field's state: one lookup at a time, last one wins. */
export function useGoToRequisition(
  onOpen: (match: RequisitionMatch) => void,
  search: SearchRequisitions = api.queue,
  sync: SyncRequisition = api.syncQuotation,
): GoToRequisition {
  const [value, setValue] = useState('')
  const [state, setState] = useState<GoToState>(IDLE)
  /*
   * A reader types the next label while the last lookup is still in flight.
   * Only the latest may answer, or a slow first scan opens over the second.
   */
  const latest = useRef(0)

  const open = (match: RequisitionMatch) => {
    latest.current += 1
    setValue('')
    setState(IDLE)
    onOpen(match)
  }

  /** One match opens; several are the person's to choose from. */
  const settle = (matches: readonly RequisitionMatch[]) => {
    const [only, ...others] = matches
    if (only !== undefined && others.length === 0 && only.inQueue) open(only)
    else setState({ status: GoToStatus.Choosing, matches, active: 0 })
  }

  /** Not in the queue: the ERP is asked, as the queue's empty state does. */
  const fetch = (wanted: WantedRequisition) => {
    const mine = (latest.current += 1)
    setState({ status: GoToStatus.Fetching })
    void fetchRequisition(wanted, sync).then((fetched) => {
      if (mine !== latest.current) return
      if (fetched.status === GoToStatus.Choosing) settle(fetched.matches)
      else setState(fetched)
    })
  }

  const pick = (match: RequisitionMatch) => {
    if (match.inQueue) open(match)
    else fetch({ quotationNumber: match.quotationNumber, branchCode: match.branchCode })
  }

  const submit = () => {
    if (state.status === GoToStatus.Choosing) {
      const chosen = state.matches[state.active]
      if (chosen !== undefined) pick(chosen)
      return
    }
    if (value.trim() === '') return
    const mine = (latest.current += 1)
    setState({ status: GoToStatus.Searching })
    findRequisitions(value, search)
      .then((found) => {
        if (mine !== latest.current) return
        if (found.status === GoToStatus.Choosing) settle(found.matches)
        else if (found.status === GoToStatus.NotFound) fetch(found.wanted)
        else setState(found)
      })
      .catch(() => {
        if (mine === latest.current) setState({ status: GoToStatus.Failed })
      })
  }

  const dismiss = () => {
    latest.current += 1
    setState(IDLE)
  }

  return {
    value,
    state,
    change: (next) => {
      setValue(next)
      // An answer is about what was typed; once that changes it is stale.
      dismiss()
    },
    submit,
    pick,
    dismiss,
    keyDown: (event) => {
      if (event.key === 'Escape') {
        dismiss()
        event.currentTarget.blur()
        return
      }
      if (state.status !== GoToStatus.Choosing) return
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
      event.preventDefault()
      setState({
        ...state,
        active: movedActive(state.active, state.matches.length, event.key),
      })
    },
  }
}
